"use client";

import type {ComponentProps} from "react";
import {SearchParamsBoundary} from "@/components/navigation/SearchParamsBoundary";
import { ChevronDown, Languages } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { i18n, type Locale } from "@/i18n-config";
import {
  getLanguageNameByLocale,
  localizePathname,
  removeLocaleFromPathname,
} from "@/utils/locales";

function DocumentationLanguageSelectContent({
  label,
  lang,
  localizedHrefs,
  searchParams,
}: {
  searchParams: URLSearchParams;
  label: string;
  lang: Locale;
  localizedHrefs?: Partial<Record<Locale, string>>;
}) {
  const pathname = usePathname();
  const router = useRouter();

  const changeLanguage = (nextLocale: Locale) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("locale", nextLocale);

    const nextPathname =
      localizedHrefs?.[nextLocale] ??
      localizePathname(
        nextLocale,
        removeLocaleFromPathname(pathname),
      );
    const query = nextSearchParams.toString();

    router.push(query ? `${nextPathname}?${query}` : nextPathname);
  };

  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
        {label}
      </span>
      <span className="relative block">
        <Languages
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400"
        />
        <select
          aria-label={label}
          className="h-10 w-full appearance-none rounded-lg border border-zinc-200 bg-white py-2 pl-9 pr-9 text-sm font-medium text-zinc-700 outline-none transition-colors hover:border-zinc-300 focus:border-grass-700/40 focus:ring-2 focus:ring-grass-500/10"
          onChange={(event) => changeLanguage(event.target.value as Locale)}
          value={lang}
        >
          {i18n.locales.map((locale) => (
            <option key={locale} value={locale}>
              {getLanguageNameByLocale(locale)}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400"
        />
      </span>
    </label>
  );
}

export function DocumentationLanguageSelect(props: Omit<ComponentProps<typeof DocumentationLanguageSelectContent>, "searchParams">) {
    return <SearchParamsBoundary>{searchParams => <DocumentationLanguageSelectContent {...props} searchParams={searchParams}/>}</SearchParamsBoundary>;
}
